/**
 * Context for collection editing callbacks
 *
 * This context provides a way for CollectionEditTemplate to trigger text updates
 * without prop drilling through PhotoCollection2 and PhotoItem2 components.
 *
 * The parent CollectionEditor component provides the implementation, which
 * handles API calls and React Query cache updates.
 *
 * @module CollectionEditContext
 */

import { createContext, useContext } from 'react';

/**
 * Context value for collection editing
 *
 * Provides callbacks that templates can use to update collection item data.
 */
export interface CollectionEditContextValue {
  /**
   * Update text content for a collection item
   *
   * @param itemId - Collection item ID (not photo ID)
   * @param text - New text content (null to remove text)
   * @returns Promise that resolves when update is complete
   *
   * @throws Error if API call fails
   *
   * @example
   * ```typescript
   * const { onUpdateText } = useCollectionEditContext();
   *
   * const handleSave = async () => {
   *   try {
   *     await onUpdateText(itemId, textValue.trim() || null);
   *   } catch (error) {
   *     console.error('Failed to save text:', error);
   *     // Revert to previous value
   *   }
   * };
   * ```
   */
  onUpdateText?: (itemId: string, text?: string) => Promise<void>;
}

/**
 * React Context for collection editing callbacks
 *
 * Default value is empty object - provider must be present for functionality.
 */
export const CollectionEditContext = createContext<CollectionEditContextValue>({});

/**
 * Hook to access collection editing context
 *
 * Use this hook in template components to access edit callbacks.
 * If no provider is present, callbacks will be undefined (no-op).
 *
 * @returns Context value with callbacks
 *
 * @example
 * ```typescript
 * export function CollectionEditTemplate({ data }: PhotoItem2TemplateProps) {
 *   const { onUpdateText } = useCollectionEditContext();
 *
 *   const handleTextBlur = () => {
 *     if (textValue !== data.textContent) {
 *       onUpdateText?.(data.itemId, textValue);
 *     }
 *   };
 *
 *   return <textarea onBlur={handleTextBlur} />;
 * }
 * ```
 */
export function useCollectionEditContext() {
  return useContext(CollectionEditContext);
}
